import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Card,
  CardBody,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DeleteOnedata,
  StringLimit,
  capitalize,
  getDataWithFilterUrl,
} from "../../controllers/APIController";
import { SearchInput } from "../../utils/helper";

import NullData from "../../components/ErrorPage/NullData";
import LoadingPage from "../../components/Loader/LoadingPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import UnifiedDeleteModal from "../../components/UnifiedDeleteModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Add CSS for line-clamp
const lineClampStyles = `
  .line-clamp-1 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
  .line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  .line-clamp-3 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
`;

const pulizirePolicyName = {
  domain: "domains",
  policy: "policies",
  procedure: "procedures",
};
const PoliciesBuilder = () => {
  const userRole = "Admin";
  const [isLoading, setisLoading] = useState(true);
  const [searchPolicyText, setSearchPolicyText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [allDomainPolicyProcedure, setAllDomainPolicyProcedure] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState([]);
  const [viewMode, setViewMode] = useState("cards"); // "table" or "cards"
  const navigate = useNavigate();

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState(null);

  // Policy details modal state
  const [isPolicyDetailsModalOpen, setIsPolicyDetailsModalOpen] =
    useState(false);
  const [selectedPolicyData, setSelectedPolicyData] = useState(null);
  const [policyDetailsLoading, setPolicyDetailsLoading] = useState(false);
  const [policyDetails, setPolicyDetails] = useState(null);

  const { _orgId: currOrg } = useParams();
  const clientQuery= useQueryClient()

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  // Handle filter selection changes
  const handleFilterSelection = (key) => {
    if (Array.from(key)?.[0] === "") {
      setSelectedFilter([]);
    } else setSelectedFilter(Array.from(key));
  };

  const handleNavigateToMetric = (item) => {
    setSelectedPolicyData(item);
    setIsPolicyDetailsModalOpen(true);
    loadPolicyDetails(item);
  };

  const deleteFormHandler = async (id, type) => {
    try {
      const collectionName = pulizirePolicyName?.[type];

      await DeleteOnedata(collectionName, id);
      clientQuery.invalidateQueries({queryKey: ["procedures", orgFilterUrl]})
      clientQuery.invalidateQueries({queryKey: ["policies", orgFilterUrl]})
      clientQuery.invalidateQueries({ queryKey: ["domains", orgFilterUrl]})
     
    } catch (error) {
      console.log(error);
    }
  };

  // Handle opening delete confirmation modal
  const handleDeleteClick = (item) => {
    setDeleteItemData({
      id: item.id,
      name: item.name || "Unnamed Item",
      type: item.type,
    });
    setIsDeleteModalOpen(true);
  };

  // Handle confirmed deletion
  const handleConfirmedDelete = async () => {
    if (deleteItemData) {
      await deleteFormHandler(deleteItemData.id, deleteItemData.type);
      setIsDeleteModalOpen(false);
      setDeleteItemData(null);
    }
  };

  // Load policy details for modal
  
  const loadPolicyDetails = async (item) => {
    setPolicyDetailsLoading(true);
    try {
      const collectionName = pulizirePolicyName?.[item.type];
      const filteredUrl = `${orgFilterUrl}&filters[id][$eqi]=${item.id}`;

      // const data = await getDataWithFilterUrl(collectionName, filteredUrl);
        const data = await clientQuery.fetchQuery({
          queryKey: [collectionName, item.id],
          queryFn: () => getDataWithFilterUrl(collectionName, filteredUrl),
        });
    
      if (data?.data?.[0]) {
        setPolicyDetails(data.data[0]);
      } else {
        setPolicyDetails(null);
      }
    } catch (error) {
      console.error("Error loading policy details:", error);
      setPolicyDetails(null);
    } finally {
      setPolicyDetailsLoading(false);
    }
  };

  // Handle closing policy details modal
  const handleClosePolicyDetailsModal = () => {
    setIsPolicyDetailsModalOpen(false);
    setSelectedPolicyData(null);
    setPolicyDetails(null);
    setPolicyDetailsLoading(false);
  };

  // Card view rendering function
  const renderCardsView = () => {
    if (filteredPolicyList.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-slate-400 text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No policies found
          </h3>
          <p className="text-slate-600 mb-4">
            No policies match your current search criteria.
          </p>
        </div>
      );
    }

    // Group policies by type
    const groupedPolicies = {
      domain: filteredPolicyList.filter((item) => item.type === "domain"),
      policy: filteredPolicyList.filter((item) => item.type === "policy"),
      procedure: filteredPolicyList.filter((item) => item.type === "procedure"),
    };

    const getTypeColor = (type) => {
      switch (type) {
        case "domain":
          return "primary";
        case "policy":
          return "success";
        case "procedure":
          return "warning";
        default:
          return "default";
      }
    };

    return (
      <div className="space-y-6">
        {/* Domain Section */}
        {groupedPolicies.domain.length > 0 && (
          <Card className="border border-emerald-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
            <CardBody className="p-0">
              <div className="px-4 py-3 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-emerald-900">
                      Policy Domains
                    </span>
                    <span className="text-sm text-emerald-600">
                      Foundation categories for ESG policies
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip
                      size="sm"
                      color="primary"
                      variant="flat"
                      className="text-xs"
                    >
                      {groupedPolicies.domain.length} total domains
                    </Chip>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedPolicies.domain.map((item, index) => (
                    <Card
                      key={item.randomId}
                      className="border border-emerald-200 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer bg-white"
                      isPressable
                      onPress={() => handleNavigateToMetric(item)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${item?.name || "domain"} details`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleNavigateToMetric(item);
                        }
                      }}
                    >
                      <CardBody className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                              #{index + 1}
                            </span>
                            <Chip
                              size="sm"
                              variant="flat"
                              color="primary"
                              className="text-xs font-medium bg-emerald-100 text-emerald-700 border-emerald-200"
                            >
                              Domain
                            </Chip>
                          </div>
                        </div>

                        <h3
                          className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2"
                          title={item?.name || "Unnamed Domain"}
                        >
                          {item?.name || "Unnamed Domain"}
                        </h3>

                        <p
                          className="text-xs text-slate-600 mb-3 line-clamp-3"
                          title={
                            item?.description || "No description available"
                          }
                        >
                          {item?.description || "No description available"}
                        </p>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              Dependencies:
                            </span>
                            <Chip
                              size="sm"
                              color={
                                item?.dependencyCount > 0
                                  ? "success"
                                  : "default"
                              }
                              variant="flat"
                              className="text-xs"
                            >
                              {item?.dependencyCount} policies
                            </Chip>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-100">
                            <Button
                              size="sm"
                              color="danger"
                              variant="light"
                              className="min-w-0 px-2 py-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(item);
                              }}
                              isDisabled={item?.dependencyCount > 0}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Policy Section */}
        {groupedPolicies.policy.length > 0 && (
          <Card className="border border-emerald-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
            <CardBody className="p-0">
              <div className="px-4 py-3 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-emerald-900">
                      Policies
                    </span>
                    <span className="text-sm text-emerald-600">
                      ESG policy frameworks and guidelines
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip
                      size="sm"
                      color="primary"
                      variant="flat"
                      className="text-xs"
                    >
                      {groupedPolicies.policy.length} total policies
                    </Chip>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedPolicies.policy.map((item, index) => (
                    <Card
                      key={item.randomId}
                      className="border border-emerald-200 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer bg-white"
                      isPressable
                      onPress={() => handleNavigateToMetric(item)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${item?.name || "policy"} details`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleNavigateToMetric(item);
                        }
                      }}
                    >
                      <CardBody className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                              #{index + 1}
                            </span>
                            <Chip
                              size="sm"
                              variant="flat"
                              color="primary"
                              className="text-xs font-medium bg-emerald-100 text-emerald-700 border-emerald-200"
                            >
                              Policy
                            </Chip>
                          </div>
                        </div>

                        <h3
                          className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2"
                          title={item?.name || "Unnamed Policy"}
                        >
                          {item?.name || "Unnamed Policy"}
                        </h3>

                        <p
                          className="text-xs text-slate-600 mb-3 line-clamp-3"
                          title={
                            item?.description || "No description available"
                          }
                        >
                          {item?.description || "No description available"}
                        </p>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              Dependencies:
                            </span>
                            <Chip
                              size="sm"
                              color={
                                item?.dependencyCount > 0
                                  ? "success"
                                  : "default"
                              }
                              variant="flat"
                              className="text-xs"
                            >
                              {item?.dependencyCount} procedures
                            </Chip>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-100">
                            <Button
                              size="sm"
                              color="danger"
                              variant="light"
                              className="min-w-0 px-2 py-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(item);
                              }}
                              isDisabled={item?.dependencyCount > 0}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Procedure Section */}
        {groupedPolicies.procedure.length > 0 && (
          <Card className="border border-emerald-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
            <CardBody className="p-0">
              <div className="px-4 py-3 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-emerald-900">
                      Procedures
                    </span>
                    <span className="text-sm text-emerald-600">
                      Operational procedures and control metrics
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip
                      size="sm"
                      color="primary"
                      variant="flat"
                      className="text-xs"
                    >
                      {groupedPolicies.procedure.length} total procedures
                    </Chip>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedPolicies.procedure.map((item, index) => (
                    <Card
                      key={item.randomId}
                      className="border border-emerald-200 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer bg-white"
                      isPressable
                      onPress={() => handleNavigateToMetric(item)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${item?.name || "procedure"} details`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleNavigateToMetric(item);
                        }
                      }}
                    >
                      <CardBody className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                              #{index + 1}
                            </span>
                            <Chip
                              size="sm"
                              variant="flat"
                              color="warning"
                              className="text-xs font-medium bg-emerald-100 text-emerald-700 border-emerald-200"
                            >
                              Procedure
                            </Chip>
                          </div>
                        </div>

                        <h3
                          className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2"
                          title={item?.name || "Unnamed Procedure"}
                        >
                          {item?.name || "Unnamed Procedure"}
                        </h3>

                        <p
                          className="text-xs text-slate-600 mb-3 line-clamp-3"
                          title={
                            item?.description || "No description available"
                          }
                        >
                          {item?.description || "No description available"}
                        </p>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              Dependencies:
                            </span>
                            <Chip
                              size="sm"
                              color="default"
                              variant="flat"
                              className="text-xs"
                            >
                              No dependencies
                            </Chip>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-100">
                            <Button
                              size="sm"
                              color="danger"
                              variant="light"
                              className="min-w-0 px-2 py-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(item);
                              }}
                              isDisabled={item?.dependencyCount > 0}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  const headers = ["Name", "Description", "Type", "Action"];

  const filteredPolicyList = allDomainPolicyProcedure.filter((item) => {
    const name = item?.name || "";
    const type = item?.type || "";
    const matchesSearchTerm =
      name.toLowerCase().includes(searchPolicyText.toLowerCase()) &&
      (selectedFilter?.length === 0 || type?.includes(selectedFilter[0]));

    return matchesSearchTerm;
  });

  const { data: domainsData } = useQuery({
    queryKey: ["domains", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("domains", orgFilterUrl),
  });
  const { data: policesData } = useQuery({
    queryKey: ["policies", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("policies", orgFilterUrl),
  });
  const { data: proceduresData } = useQuery({
    queryKey: ["procedures", orgFilterUrl],
    queryFn: () => getDataWithFilterUrl("procedures", orgFilterUrl),
  });
  useEffect(() => {
    const li = [];
    let randomId = 0;
   
    domainsData?.data?.forEach((element) => {
      li.push({
        randomId: randomId++,
        id: element.id,
        name: element?.attributes?.Name,
        description: element?.attributes?.Description || "--",
        type: "domain",
        dependencyCount: element?.attributes?.policies?.data?.length || 0,
      });
    });

    policesData?.data?.forEach((element) => {
      li.push({
        randomId: randomId++,
        id: element.id,
        name: element?.attributes?.Name,
        description: element?.attributes?.Description || "--",
        type: "policy",
        dependencyCount: element?.attributes?.procedures?.data?.length || 0,
      });
    });
    proceduresData?.data?.forEach((element) => {
      li.push({
        randomId: randomId++,
        id: element.id,
        name: element?.attributes?.Name,
        description: element?.attributes?.Description || "--",
        type: "procedure",
        dependencyCount: 0,
      });
    });

    setAllDomainPolicyProcedure(li);
    setisLoading(false);
  }, [proceduresData, policesData, domainsData]);

  const ItemPerPage = 10;
  const totalPage = Math.ceil(filteredPolicyList.length / ItemPerPage);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Inject CSS styles */}
      <style>{lineClampStyles}</style>

      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">
              Policies Builder
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Create and manage ESG policies for your organization
            </p>
          </div>
          <Button
            color="primary"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 font-medium"
            onClick={() => navigate("create")}
          >
            Create
          </Button>
        </div>
      </div>

      <div className="w-full px-4 py-2">
        {/* Overview Card */}
        {/* <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-3">Policy Management</h2>
            <p className="text-neutral-600 leading-relaxed">
              Create or edit ESG Policies that would support your ESG Reporting.
              Build comprehensive policy frameworks to guide your sustainability initiatives.
            </p>
          </div> */}
        {/* Filters and Content */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  Policy Repository
                  {viewMode === "cards" && (
                    <span className="ml-2 text-sm font-normal text-neutral-500">
                      ({filteredPolicyList.length} total items)
                    </span>
                  )}
                </h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {viewMode === "table"
                    ? "Browse and manage domains, policies, and procedures in table format"
                    : "Browse and manage domains, policies, and procedures organized by type"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* View Toggle Buttons */}
                <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                  <Button
                    size="sm"
                    variant={viewMode === "table" ? "solid" : "light"}
                    color={viewMode === "table" ? "primary" : "default"}
                    className={`px-3 py-2 min-w-0 ${
                      viewMode === "table"
                        ? "bg-emerald-600 text-white"
                        : "text-neutral-600"
                    }`}
                    onClick={() => setViewMode("table")}
                  >
                    Table
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === "cards" ? "solid" : "light"}
                    color={viewMode === "cards" ? "primary" : "default"}
                    className={`px-3 py-2 min-w-0 ${
                      viewMode === "cards"
                        ? "bg-emerald-600 text-white"
                        : "text-neutral-600"
                    }`}
                    onClick={() => setViewMode("cards")}
                  >
                    Cards
                  </Button>
                </div>

                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      variant="ghost"
                      endContent={<FontAwesomeIcon icon={faFilter} />}
                      className="text-neutral-600 hover:text-neutral-900 px-4"
                    >
                      {selectedFilter?.length > 0
                        ? capitalize(selectedFilter[0])
                        : "Filter"}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Filter selection"
                    selectionMode="single"
                    onSelectionChange={handleFilterSelection}
                    selectedKeys={new Set(selectedFilter)}
                  >
                    <DropdownItem key="">Clear Filters</DropdownItem>
                    <DropdownItem key="domain" color="primary" variant="flat">
                      Domain
                    </DropdownItem>
                    <DropdownItem key="policy" color="success" variant="flat">
                      Policy
                    </DropdownItem>
                    <DropdownItem
                      key="procedure"
                      variant="flat"
                      color="warning"
                    >
                      Procedure
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </div>
          <div className="p-6">
            <LoadingPage
              isLoading={isLoading}
              loadingText="Loading policies..."
            >
              {!isLoading && (
                <>
                  <div className="my-4 w-full">
                    <SearchInput
                      searchText={searchPolicyText}
                      setSearchText={setSearchPolicyText}
                      placeholderText={"Search for an any ESG Policies"}
                    />
                  </div>

                  {filteredPolicyList.length === 0 ? (
                    <NullData />
                  ) : (
                    <div className="">
                      {viewMode === "table" ? (
                        // Table View
                        <div className="mt-4">
                          {filteredPolicyList.length > 0 && (
                            <div className="">
                              <Table
                                removeWrapper
                                selectionMode="single"
                                aria-label="all-policies-table"
                              >
                                <TableHeader>
                                  {headers.map((header, headerIndex) => (
                                    <TableColumn key={headerIndex}>
                                      {header}
                                    </TableColumn>
                                  ))}
                                </TableHeader>
                                <TableBody>
                                  {filteredPolicyList.map((item, index) => {
                                    const isDeletableItem = !(
                                      item?.dependencyCount > 0
                                    );

                                    const DropDownText = isDeletableItem
                                      ? `Delete ${item?.name} ?`
                                      : "Can't be deleted due to dependency";
                                    const lowerPageLimit =
                                      (currentPage - 1) * ItemPerPage;
                                    const upperPageLimit =
                                      currentPage * ItemPerPage;
                                    if (
                                      index < lowerPageLimit ||
                                      index >= upperPageLimit
                                    )
                                      return null;

                                    return (
                                      <TableRow
                                        key={item.randomId}
                                        onClick={() =>
                                          handleNavigateToMetric(item)
                                        }
                                        className="cursor-pointer"
                                      >
                                        <TableCell>{item?.name}</TableCell>

                                        <TableCell>
                                          {StringLimit(
                                            item?.description,
                                            100
                                          ) || "--"}
                                        </TableCell>
                                        <TableCell>
                                          {capitalize(item?.type)}
                                        </TableCell>
                                        <TableCell>
                                          <Button
                                            size="sm"
                                            color="danger"
                                            variant="light"
                                            className="min-w-0 px-2 py-1 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isDeletableItem) {
                                                handleDeleteClick(item);
                                              }
                                            }}
                                            isDisabled={!isDeletableItem}
                                          >
                                            Delete
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Card View
                        <div className="mt-4">{renderCardsView()}</div>
                      )}

                      {/* Pagination - Only show for table view */}
                      {viewMode === "table" &&
                        filteredPolicyList.length > 0 && (
                          <div className="w-full mt-8 flex items-center justify-center">
                            <Pagination
                              showControls
                              total={totalPage}
                              color="secondary"
                              page={currentPage}
                              onChange={setCurrentPage}
                            />
                          </div>
                        )}
                    </div>
                  )}
                </>
              )}
            </LoadingPage>
          </div>
        </div>
      </div>

      {/* Policy Details Modal */}
      <Modal
        isOpen={isPolicyDetailsModalOpen}
        onOpenChange={handleClosePolicyDetailsModal}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <Chip
                    size="sm"
                    color="primary"
                    variant="flat"
                    className="text-xs font-medium bg-emerald-100 text-emerald-700 border-emerald-200"
                  >
                    {selectedPolicyData?.type
                      ? capitalize(selectedPolicyData.type)
                      : "Item"}
                  </Chip>
                  <h3 className="text-lg font-semibold">
                    {selectedPolicyData?.name || "Unnamed Item"}
                  </h3>
                </div>
              </ModalHeader>
              <ModalBody>
                {policyDetailsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">
                        Loading details...
                      </p>
                    </div>
                  </div>
                ) : policyDetails ? (
                  <div className="space-y-4">
                    {/* Basic Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Description
                      </h4>
                      <p className="text-sm text-gray-700">
                        {policyDetails?.attributes?.Description ||
                          "No description available"}
                      </p>
                    </div>

                    {/* Additional Details based on type */}
                    {selectedPolicyData?.type === "domain" && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Domain Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Created:</span>
                            <span className="ml-2 text-gray-900">
                              {new Date(
                                policyDetails?.attributes?.createdAt
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Updated:</span>
                            <span className="ml-2 text-gray-900">
                              {new Date(
                                policyDetails?.attributes?.updatedAt
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPolicyData?.type === "policy" && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Policy Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Domain:</span>
                            <span className="ml-2 text-gray-900">
                              {policyDetails?.attributes?.DomainId?.data
                                ?.attributes?.Name || "Not specified"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Framework:</span>
                            <span className="ml-2 text-gray-900">
                              {policyDetails?.attributes?.PolicyFramework
                                ? "Available"
                                : "Not available"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPolicyData?.type === "procedure" && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Procedure Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Policy:</span>
                            <span className="ml-2 text-gray-900">
                              {policyDetails?.attributes?.PolicyId?.data
                                ?.attributes?.Name || "Not specified"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">
                              Control Metrics:
                            </span>
                            <span className="ml-2 text-gray-900">
                              {policyDetails?.attributes?.ControlMetrics
                                ?.controlMetrics?.length || 0}{" "}
                              metrics
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Raw Data (for debugging) */}
                    {process.env.NODE_ENV === "development" && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Raw Data (Development)
                        </h4>
                        <pre className="text-xs text-gray-600 overflow-auto max-h-32">
                          {JSON.stringify(policyDetails, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">
                      No details available for this item.
                    </p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="default" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button
                  color="primary"
                  onPress={() => {
                    onClose();
                    // Navigate to edit page if needed
                    navigate(
                      `/${currOrg}/policies-builder/${selectedPolicyData?.id}?type=${selectedPolicyData?.type}`
                    );
                  }}
                >
                  Edit
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <UnifiedDeleteModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={handleConfirmedDelete}
        title={`Delete ${
          deleteItemData?.type ? capitalize(deleteItemData.type) : "Item"
        }`}
        message={`Are you sure you want to delete this ${
          deleteItemData?.type || "item"
        }?`}
        itemName={deleteItemData?.name}
        itemType={deleteItemData?.type}
      />
    </div>
  );
};

export default PoliciesBuilder;
